import React, { useState, useEffect } from 'react';
import { getOrders, printOrderDocument } from '../../../lib/api';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Receipt, RefreshCw, Loader2, Printer, Search, CreditCard, Banknote, ChevronRight } from 'lucide-react';
import { getPaymentMethodLabel, isCashPaymentMethod } from '../../../lib/paymentMethods';
import { toast } from 'sonner';

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

async function fetchInvoices(filterDate) {
  return getOrders('completed', filterDate || null);
}

export default function AdminReportsInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().slice(0, 10));
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [printingId, setPrintingId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadForDate() {
      setIsLoading(true);
      try {
        const data = await fetchInvoices(filterDate);
        if (!cancelled) {
          setInvoices(data);
        }
      } catch {
        if (!cancelled) {
          toast.error('Fehler beim Laden');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadForDate();
    return () => {
      cancelled = true;
    };
  }, [filterDate]);

  const loadInvoices = async () => {
    setIsLoading(true);
    try {
      setInvoices(await fetchInvoices(filterDate));
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = invoices.filter(inv => {
    if (!search) return true;
    const num = String(inv.order_number || inv.id || '');
    const table = String(inv.table_name || inv.table_id || '');
    return num.includes(search) || table.toLowerCase().includes(search.toLowerCase());
  });

  const totalRevenue = filtered.reduce((s, o) => s + (o.total || 0), 0);
  const cashTotal = filtered.filter(o => isCashPaymentMethod(o.payment_method)).reduce((s, o) => s + (o.total || 0), 0);
  const cardTotal = filtered.filter(o => !isCashPaymentMethod(o.payment_method)).reduce((s, o) => s + (o.total || 0), 0);

  const printInvoice = async (inv) => {
    if (!inv?.id) return;
    setPrintingId(inv.id);
    try {
      const result = await printOrderDocument(inv.id, 'invoice');
      toast.success(result?.message || 'Druckauftrag gesendet');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Druckauftrag fehlgeschlagen');
    } finally {
      setPrintingId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Belege</h1>
          <p className="text-slate-500">{filtered.length} Belege · €{totalRevenue.toFixed(2)} Umsatz</p>
        </div>
        <Button variant="outline" onClick={loadInvoices}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Gesamtumsatz', value: `€${totalRevenue.toFixed(2)}`, icon: Receipt, color: 'text-[#1a2f38] bg-slate-100' },
          { label: 'Bargeld',      value: `€${cashTotal.toFixed(2)}`,    icon: Banknote, color: 'text-emerald-700 bg-emerald-50' },
          { label: 'Karte',        value: `€${cardTotal.toFixed(2)}`,    icon: CreditCard, color: 'text-blue-700 bg-blue-50' },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-500">{label}</p>
                <p className="text-xl font-bold text-slate-900 tabular-nums">{value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="w-44"
        />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Tisch oder Nr. suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 w-52"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-7 h-7 animate-spin text-[#1a2f38]" />
        </div>
      ) : (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Beleg-Nr.</TableHead>
                  <TableHead>Datum & Zeit</TableHead>
                  <TableHead>Tisch</TableHead>
                  <TableHead>Zahlung</TableHead>
                  <TableHead className="text-right">Betrag</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((inv) => (
                  <TableRow
                    key={inv.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setSelected(inv)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-slate-400" />
                        <span className="font-mono font-medium">
                          #{inv.order_number || inv.id?.slice(-6)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm tabular-nums">
                      {formatDateTime(inv.created_at)}
                    </TableCell>
                    <TableCell className="text-slate-600">{inv.table_name || inv.table_id || '—'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-slate-600">
                        {isCashPaymentMethod(inv.payment_method)
                          ? <><Banknote className="w-3.5 h-3.5 text-emerald-600" /> <span className="text-sm">{getPaymentMethodLabel(inv.payment_method)}</span></>
                          : <><CreditCard className="w-3.5 h-3.5 text-blue-600" /> <span className="text-sm">{getPaymentMethodLabel(inv.payment_method)}</span></>
                        }
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      €{(inv.total || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={printingId === inv.id}
                          onClick={(e) => { e.stopPropagation(); printInvoice(inv); }}
                        >
                          {printingId === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                        </Button>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filtered.length === 0 && (
              <div className="text-center py-12">
                <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">Keine Belege für dieses Datum</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invoice detail */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Beleg #{selected?.order_number || selected?.id?.slice(-6)}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 pt-2 text-sm">
              <div className="text-center border-b pb-3">
                <p className="text-xs text-slate-400">FiscalPOS · {formatDateTime(selected.created_at)}</p>
                <p className="text-xs text-slate-400">Tisch: {selected.table_name || selected.table_id || '—'}</p>
              </div>

              <div className="space-y-1.5">
                {(selected.items || []).map((item, i) => (
                  <div key={i} className="flex justify-between">
                    <span className="text-slate-600">{item.quantity}× {item.product_name || item.name}</span>
                    <span className="font-mono">€{((item.price || 0) * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {selected.discount_amount > 0 && (
                <div className="flex justify-between text-red-600 border-t pt-2">
                  <span>Rabatt</span>
                  <span className="font-mono">-€{selected.discount_amount.toFixed(2)}</span>
                </div>
              )}

              <div className="border-t pt-2 flex justify-between font-bold text-base">
                <span>Gesamt</span>
                <span className="font-mono">€{(selected.total || 0).toFixed(2)}</span>
              </div>

              <div className="text-center text-xs text-slate-400 border-t pt-2">
                <p>Zahlung: {getPaymentMethodLabel(selected.payment_method)}</p>
                {selected.ft_qr_data && (
                  <p className="mt-1 font-mono break-all">{selected.ft_qr_data}</p>
                )}
              </div>

              <Button
                className="w-full bg-[#1a2f38]"
                disabled={printingId === selected.id}
                onClick={() => printInvoice(selected)}
              >
                {printingId === selected.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                Drucken
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
